/**
 * DeletedUserRow
 *
 * Row renderer for deleted users table with robust parsing of legacy fields
 * and safe fallbacks. Delegates delete action via callback.
 */
import React, { useState } from 'react';
import { Calendar, Clock, FileText, User, Video, Download, Play, Trash2 } from 'lucide-react';
import Tooltip from '../../ui/overlays/Tooltip';

// Helper function to format numbers with commas
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

// Helper function to safely get values
const safeValue = (value, fallback = 'N/A') => {
  return value || fallback;
};

// Helper function to safely get numbers
const safeNumber = (value, fallback = 0) => {
  return Number(value) || fallback;
};

// Helper function to get subscription status color
const getSubscriptionStatusColor = (status) => {
  const statusColors = {
    'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'trialing': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    'past_due': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    'canceled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'incomplete': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'incomplete_expired': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'unpaid': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  };
  return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const DeletedUserRow = ({ 
  deletedUser, 
  formatDate,
  formatExactDateTime,
  onDeleteUser,
  deletingUserId = null // ID of the user currently being deleted
}) => {
  const isDeleting = deletingUserId === deletedUser.id;
  // Helper functions from DeletedUsers page
  const getCalculatedMetrics = (user) => {
    // Check if we have corrupted account creation data
    let accountCreated = null;
    let deletedAt = null;
    
    try {
      accountCreated = user.accountCreatedAt?.toDate?.() || (user.accountCreatedAt ? new Date(user.accountCreatedAt) : null);
      deletedAt = user.deletedAt?.toDate?.() || (user.deletedAt ? new Date(user.deletedAt) : null);
    } catch (error) {
      console.warn('Error parsing dates in DeletedUserRow:', error);
      accountCreated = null;
      deletedAt = null;
    }
    
    const isCorruptedData = accountCreated && deletedAt && 
      accountCreated instanceof Date && deletedAt instanceof Date &&
      Math.abs(accountCreated.getTime() - deletedAt.getTime()) < 60000; // Within 1 minute = corrupted
    
    // Check if we have the new activity span fields (they won't exist for old records)
    const hasActivitySpanData = user.activitySpanDays !== undefined && user.activitySpanDays !== null;
    
    return {
      // New engagement metrics (clean slate - no fallbacks to old data)
      currentStreak: safeNumber(user.currentStreak, 0),
      longestStreak: safeNumber(user.longestStreak, 0),
      activeDaysThisMonth: safeNumber(user.activeDaysThisMonth, 0),
      activeDaysThisWeek: safeNumber(user.activeDaysThisWeek, 0),
      membershipDays: isCorruptedData ? null : safeNumber(user.membershipDays, null),
      activitySpanDays: hasActivitySpanData ? safeNumber(user.activitySpanDays, 0) : null,
      usageSpanDays: hasActivitySpanData ? safeNumber(user.usageSpanDays, 0) : null,
      membershipLength: user.membershipLength || formatActivityDuration(
        isCorruptedData ? null : user.membershipDays, 
        hasActivitySpanData ? user.activitySpanDays : null, 
        user.activeDays
      ),
      enhancedDuration: formatActivityDuration(
        isCorruptedData ? null : user.membershipDays, 
        hasActivitySpanData ? user.activitySpanDays : null, 
        user.activeDays
      )
    };
  };

  const getPaidUserStatus = (user) => {
    // Simple and reliable payment detection
    
    // 1. Use Stripe-verified payment data (most reliable)
    if (user.hasEverPaid !== undefined) {
      return user.hasEverPaid;
    }
    
    // 2. Use revenue data (definitive proof of payment)
    const totalRevenue = Number(user.totalRevenue) || 0;
    const monthsAsPayingCustomer = Number(user.monthsAsPayingCustomer) || 0;
    
    return totalRevenue > 0 || monthsAsPayingCustomer > 0;
  };

  // Enhanced duration formatting function
  const formatActivityDuration = (days, spanDays, activeDays) => {
    // If no reliable creation date OR no reliable span data, return N/A
    // Also check if accountCreatedAt equals deletedAt (corrupted data)
    let accountCreated = null;
    let deletedAt = null;
    
    try {
      accountCreated = deletedUser.accountCreatedAt?.toDate?.() || (deletedUser.accountCreatedAt ? new Date(deletedUser.accountCreatedAt) : null);
      deletedAt = deletedUser.deletedAt?.toDate?.() || (deletedUser.deletedAt ? new Date(deletedUser.deletedAt) : null);
    } catch (error) {
      console.warn('Error parsing dates in formatActivityDuration:', error);
      accountCreated = null;
      deletedAt = null;
    }
    
    if (!accountCreated || 
        spanDays === null || 
        spanDays === undefined ||
        !deletedUser.activitySpanDays ||
        (accountCreated && deletedAt && accountCreated instanceof Date && deletedAt instanceof Date &&
         Math.abs(accountCreated.getTime() - deletedAt.getTime()) < 60000)) { // Within 1 minute = corrupted
      return 'N/A';
    }
    
    // Priority: Use activity span if available and reasonable, otherwise fall back
    const primaryDays = safeNumber(spanDays, 0);
    const fallbackDays = safeNumber(days, 0);
    const actualActiveDays = safeNumber(activeDays, 0);
    
    const daysToUse = primaryDays > 0 ? primaryDays : fallbackDays;
    
    // If we have activity span data, use that for a more accurate picture
    if (primaryDays > 0 && actualActiveDays > 0) {
      const intensityText = actualActiveDays > (primaryDays * 0.8) ? ' (high usage)' : 
                           actualActiveDays > (primaryDays * 0.4) ? ' (moderate usage)' : 
                           ' (light usage)';
      
      if (daysToUse === 0) return '< 1 day' + intensityText;
      if (daysToUse === 1) return '1 day' + intensityText;
      if (daysToUse < 7) return `${daysToUse} days` + intensityText;
      if (daysToUse < 30) {
        const weeks = Math.floor(daysToUse / 7);
        const remainingDays = daysToUse % 7;
        if (weeks === 1 && remainingDays === 0) return '1 week' + intensityText;
        if (remainingDays === 0) return `${weeks} weeks` + intensityText;
        return `${weeks}w ${remainingDays}d` + intensityText;
      }
      if (daysToUse < 365) {
        const months = Math.floor(daysToUse / 30);
        const remainingDays = daysToUse % 30;
        if (months === 1 && remainingDays <= 7) return '1 month' + intensityText;
        if (remainingDays <= 7) return `${months} months` + intensityText;
        return `${months}m ${Math.floor(remainingDays / 7)}w` + intensityText;
      }
      const years = Math.floor(daysToUse / 365);
      const remainingMonths = Math.floor((daysToUse % 365) / 30);
      return `${years}y ${remainingMonths}m` + intensityText;
    }
    
    // Fallback to simpler formatting
    if (daysToUse === 0) return '< 1 day';
    if (daysToUse === 1) return '1 day';
    if (daysToUse < 7) return `${daysToUse} days`;
    if (daysToUse < 30) {
      const weeks = Math.floor(daysToUse / 7);
      const remainingDays = daysToUse % 7;
      if (weeks === 1 && remainingDays === 0) return '1 week';
      if (remainingDays === 0) return `${weeks} weeks`;
      return `${weeks}w ${remainingDays}d`;
    }
    if (daysToUse < 365) {
      const months = Math.floor(daysToUse / 30);
      return months === 1 ? '1 month' : `${months} months`;
    }
    const years = Math.floor(daysToUse / 365);
    const remainingMonths = Math.floor((daysToUse % 365) / 30);
    return `${years}y ${remainingMonths}m`;
  };

  const getSubscriptionStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-400';
    
    const statusLower = status.toLowerCase();
    switch(statusLower) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'trialing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'canceled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-400';
    }
  };

  // Parse exit survey data to extract different reasons
  const parseExitSurveyData = (exitSurveyData) => {
    const defaultReasons = {
      primaryReason: 'N/A',
      secondaryReason: 'N/A',
      feedback: 'N/A'
    };

    if (!exitSurveyData) return defaultReasons;
    
    try {
      let parsed;
      if (typeof exitSurveyData === 'string') {
        parsed = JSON.parse(exitSurveyData);
      } else {
        parsed = exitSurveyData;
      }
      
      // Support both shapes:
      // 1) { primaryReason, secondaryReason, feedback }
      // 2) { reason, improvements: [], feedback }
      const primaryReason = parsed.primaryReason || parsed.reason || parsed.main_reason || 'Not provided';
      const secondaryReason = parsed.secondaryReason ||
        (Array.isArray(parsed.improvements) && parsed.improvements.length ? parsed.improvements.join(', ') : (parsed.additional_reason || parsed.other_reason || 'N/A'));
      const feedback = parsed.feedback || parsed.comments || parsed.additional_feedback || 'N/A';
      
      return { primaryReason, secondaryReason, feedback };
    } catch (error) {
      // If parsing fails, treat as simple string
      if (typeof exitSurveyData === 'string' && exitSurveyData.length > 0) {
        return {
          primaryReason: exitSurveyData.length > 50 ? exitSurveyData.substring(0, 50) + '...' : exitSurveyData,
          secondaryReason: 'N/A',
          feedback: 'N/A'
        };
      }
      return defaultReasons;
    }
  };

  const metrics = getCalculatedMetrics(deletedUser);
  const paidStatus = getPaidUserStatus(deletedUser);
  const exitReasons = parseExitSurveyData(deletedUser.exitSurveyData);

  const handleDelete = () => {
    if (!onDeleteUser) return;
    // Pass the user data to parent to handle confirmation modal
    onDeleteUser(deletedUser.id, deletedUser.originalUserId, deletedUser);
  };

  return (
    <tr key={deletedUser.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
      {/* Name Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {safeValue(deletedUser.displayName, 'Unknown User')}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {deletedUser.firstName && deletedUser.lastName ? 
                `${deletedUser.firstName} ${deletedUser.lastName}` : 
                'No name set'
              }
            </div>
          </div>
        </div>
      </td>

      {/* Email Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {safeValue(deletedUser.email)}
        </div>
        {deletedUser.isEmailVerified && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 mt-1">
            Verified
          </span>
        )}
      </td>

      {/* Plan Level Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          (deletedUser.planLevel || '').toLowerCase().includes('premium') 
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-400'
        }`}>
          {safeValue(deletedUser.planLevel, 'Basic')}
        </span>
      </td>

      {/* Paid Customer Column */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <div className="space-y-1">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
            paidStatus 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {paidStatus ? 'Yes' : 'No'}
          </span>
          {paidStatus && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {safeNumber(deletedUser.totalRevenue) > 0 && (
                <div>${formatNumber(safeNumber(deletedUser.totalRevenue))}</div>
              )}
              {safeNumber(deletedUser.monthsAsPayingCustomer) > 0 && (
                <div>{safeNumber(deletedUser.monthsAsPayingCustomer)} month{safeNumber(deletedUser.monthsAsPayingCustomer) !== 1 ? 's' : ''}</div>
              )}
            </div>
          )}
        </div>
      </td>

      {/* Account Created Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        {(() => {
          let accountCreated = null;
          let deletedAt = null;
          
          try {
            accountCreated = deletedUser.accountCreatedAt?.toDate?.() || (deletedUser.accountCreatedAt ? new Date(deletedUser.accountCreatedAt) : null);
            deletedAt = deletedUser.deletedAt?.toDate?.() || (deletedUser.deletedAt ? new Date(deletedUser.deletedAt) : null);
          } catch (error) {
            console.warn('Error parsing dates in Account Created column:', error);
            accountCreated = null;
            deletedAt = null;
          }
          
          const isCorrupted = accountCreated && deletedAt && 
            accountCreated instanceof Date && deletedAt instanceof Date &&
            Math.abs(accountCreated.getTime() - deletedAt.getTime()) < 60000; // Within 1 minute = corrupted
          
          if (!accountCreated || isCorrupted) {
            return (
              <Tooltip content="Account creation date unknown (data not available for historical records)" id={`created-deleted-${deletedUser.originalUserId || 'unknown'}`}>
                <span className="text-gray-400 dark:text-gray-500 cursor-help">N/A</span>
              </Tooltip>
            );
          }
          
          return (
            <Tooltip content={formatExactDateTime(deletedUser.accountCreatedAt)} id={`created-deleted-${deletedUser.originalUserId || 'unknown'}`}>
              <div className="cursor-help">{formatDate(deletedUser.accountCreatedAt)}</div>
            </Tooltip>
          );
        })()}
      </td>

      {/* Deleted At Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <Tooltip content={formatExactDateTime(deletedUser.deletedAt)} id={`deleted-at-${deletedUser.originalUserId || 'unknown'}`}>
          <div className="cursor-help">{formatDate(deletedUser.deletedAt)}</div>
        </Tooltip>
      </td>

      {/* Engagement Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <div className="flex flex-col items-center space-y-1">
          <div className="flex items-center">
            <Calendar className="h-3 w-3 text-gray-400 mr-1" />
            <span className="text-xs text-gray-900 dark:text-white font-medium">
              🔥 {formatNumber(metrics.currentStreak)} streak
            </span>
          </div>
          <div className="flex items-center">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              📅 {formatNumber(metrics.activeDaysThisMonth)}/mo
            </span>
          </div>
          <div className="flex items-center">
            <span className="text-xs text-gray-500 dark:text-gray-500">
              🏆 {formatNumber(metrics.longestStreak)} best
            </span>
          </div>
        </div>
      </td>

      {/* Regular Templates Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <div className="flex items-center justify-center">
          <FileText className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900 dark:text-white font-medium">
            {formatNumber(safeNumber(deletedUser.totalTemplatesEdited, 0))}
          </span>
        </div>
      </td>

      {/* Video Templates Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <div className="flex items-center justify-center">
          <Video className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900 dark:text-white font-medium">
            {formatNumber(safeNumber(deletedUser.totalVideoTemplatesEdited, 0))}
          </span>
        </div>
      </td>

      {/* Stock Videos Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <div className="flex items-center justify-center">
          <Download className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900 dark:text-white font-medium">
            {formatNumber(safeNumber(deletedUser.totalStockVideosDownloaded, 0))}
          </span>
        </div>
      </td>

      {/* Campaigns Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <div className="flex items-center justify-center">
          <FileText className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900 dark:text-white font-medium">
            {formatNumber(safeNumber(deletedUser.campaignsCreated, 0))}
          </span>
        </div>
      </td>

      {/* Survey Videos Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        <div className="flex items-center justify-center">
          <Play className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900 dark:text-white font-medium">
            {formatNumber(safeNumber(deletedUser.surveyVideosCollected, 0))}
          </span>
        </div>
      </td>

      {/* Subscription Status Column */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        {deletedUser.detailedSubscriptionStatus ? (
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getSubscriptionStatusColor(deletedUser.detailedSubscriptionStatus)}`}>
            {deletedUser.detailedSubscriptionStatus}
          </span>
        ) : (
          <span className="text-sm text-gray-500 dark:text-gray-400">N/A</span>
        )}
      </td>

      {/* Last Active Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
        {(() => {
          try {
            const lastActive = deletedUser.lastActive?.toDate?.() || (deletedUser.lastActive ? new Date(deletedUser.lastActive) : null);
            if (!lastActive) {
              return <span className="text-gray-400 dark:text-gray-500">N/A</span>;
            }
            return (
              <Tooltip content={formatExactDateTime(deletedUser.lastActive)} id={`last-active-${deletedUser.originalUserId || 'unknown'}`}>
                <div className="cursor-help">{formatDate(deletedUser.lastActive)}</div>
              </Tooltip>
            );
          } catch (error) {
            return <span className="text-gray-400 dark:text-gray-500">N/A</span>;
          }
        })()}
      </td>

      {/* Email Verified Column */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          deletedUser.isEmailVerified 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {deletedUser.isEmailVerified ? 'Yes' : 'No'}
        </span>
      </td>

      {/* Primary Reason Column */}
      <td className="px-6 py-4 text-center">
        <Tooltip 
          content={exitReasons.primaryReason !== 'N/A' ? exitReasons.primaryReason : 'No primary reason provided'} 
          id={`primary-reason-${deletedUser.originalUserId || 'unknown'}`}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400 cursor-help">
            {exitReasons.primaryReason}
          </div>
        </Tooltip>
      </td>

      {/* Secondary Reason Column */}
      <td className="px-6 py-4 text-center">
        <Tooltip 
          content={exitReasons.secondaryReason !== 'N/A' ? exitReasons.secondaryReason : 'No secondary reason provided'} 
          id={`secondary-reason-${deletedUser.originalUserId || 'unknown'}`}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400 cursor-help">
            {exitReasons.secondaryReason}
          </div>
        </Tooltip>
      </td>

      {/* Feedback Column */}
      <td className="px-6 py-4 text-center w-80 min-w-80">
        <Tooltip 
          content={exitReasons.feedback !== 'N/A' ? exitReasons.feedback : 'No feedback provided'} 
          id={`feedback-${deletedUser.originalUserId || 'unknown'}`}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400 cursor-help break-words leading-relaxed">
            {exitReasons.feedback}
          </div>
        </Tooltip>
      </td>

      {/* Deletion Source Column */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          deletedUser.deletionSource === 'self_deletion' 
            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
            : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
        }`}>
          {deletedUser.deletionSource === 'self_deletion' ? 'Self' : 'Admin'}
        </span>
      </td>

      {/* Actions Column */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md transition-colors ${
            isDeleting
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
          }`}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          {isDeleting ? 'Removing...' : 'Remove'}
        </button>
      </td>
    </tr>
  );
};

export default DeletedUserRow; 